'use client';

import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useClientBrand } from '@/context/client-hostname-provider';
import reconcileFavorites from '@/services/reconcile-favorites';
import {
  addFavorite,
  FAVORITES_RECONCILE_THROTTLE_MS,
  getFavoritesReconciledAtKey,
  getFavoritesStorageKey,
  LEGACY_FAVORITES_RECONCILED_AT_KEY,
  LEGACY_FAVORITES_STORAGE_KEY,
  removeFavorite,
  writeFavorites,
} from '@/utils/favorites';
import { computeStaleFavoriteIds } from '@/utils/favorites-reconcile';
import { readLocalStorage } from '@/utils/local-storage/read-local-storage';
import { removeLocalStorageItem } from '@/utils/local-storage/safe-local-storage';
import { writeToLocalStorage } from '@/utils/local-storage/write-to-local-storage';
import { sendToSentry } from '@/utils/sentry';

const parseFavoriteIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value.filter(
        (id): id is string => typeof id === 'string' && id.length > 0
      )
    )
  );
};

const wasReconciledRecently = (brandKey: string) => {
  const lastReconciledAt = readLocalStorage(
    getFavoritesReconciledAtKey(brandKey)
  );
  return (
    typeof lastReconciledAt === 'number' &&
    Date.now() - lastReconciledAt < FAVORITES_RECONCILE_THROTTLE_MS
  );
};

const migrateLegacyFavoritesIfNeeded = (brandKey: string) => {
  const storageKey = getFavoritesStorageKey(brandKey);
  const existing = parseFavoriteIds(readLocalStorage(storageKey));
  if (existing.length) {
    return existing;
  }

  const legacy = parseFavoriteIds(
    readLocalStorage(LEGACY_FAVORITES_STORAGE_KEY)
  );
  if (!legacy.length) {
    return [];
  }

  writeFavorites(brandKey, legacy);
  removeLocalStorageItem(LEGACY_FAVORITES_STORAGE_KEY);

  const legacyReconciledAt = readLocalStorage(
    LEGACY_FAVORITES_RECONCILED_AT_KEY
  );
  if (typeof legacyReconciledAt === 'number') {
    writeToLocalStorage(
      getFavoritesReconciledAtKey(brandKey),
      legacyReconciledAt
    );
    removeLocalStorageItem(LEGACY_FAVORITES_RECONCILED_AT_KEY);
  }

  return legacy;
};

interface FavoritesContextType {
  favoritesCount: number;
  favoritesIds: string[];
  addToFavorites: (listing: string) => void;
  removeFromFavorites: (listing: string) => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(
  undefined
);

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({
  children,
}) => {
  const { brand } = useClientBrand();
  const brandKey = brand.key;
  const [favoritesIds, setFavoritesIds] = useState<string[]>([]);
  const favoritesCount = useMemo(() => favoritesIds.length, [favoritesIds]);

  const addToFavorites = (listingId: string) => {
    if (listingId) {
      setFavoritesIds((prev) =>
        prev.includes(listingId) ? prev : [...prev, listingId]
      );
      addFavorite(brandKey, listingId);
    }
  };

  const removeFromFavorites = (listingId: string) => {
    if (listingId) {
      setFavoritesIds((prev) => prev.filter((id) => id !== listingId));
      removeFavorite(brandKey, listingId);
    }
  };

  useEffect(() => {
    const favorites = migrateLegacyFavoritesIfNeeded(brandKey);
    if (favorites.length) {
      setFavoritesIds(favorites);
    } else {
      setFavoritesIds([]);
    }

    if (!favorites.length || wasReconciledRecently(brandKey)) {
      return;
    }

    let cancelled = false;

    reconcileFavorites(favorites).then((result) => {
      if (cancelled || result.status !== 'ok') {
        return;
      }

      const computation = computeStaleFavoriteIds({
        savedIds: favorites,
        returned: result.listings,
        complete: result.complete,
        ignoreShowOnWebsite: result.ignoreShowOnWebsite,
      });

      if (computation.skippedMassWipe) {
        sendToSentry(
          null,
          'Skipped favorites mass wipe during reconcile',
          'favorites',
          'reconcile-mass-wipe',
          {
            savedCount: favorites.length,
            reasons: computation.reasons,
          }
        );
        return;
      }

      writeToLocalStorage(getFavoritesReconciledAtKey(brandKey), Date.now());

      if (computation.staleIds.length === 0) {
        return;
      }

      const staleSet = new Set(computation.staleIds);
      const nextIds = favorites.filter((id) => !staleSet.has(id));
      writeFavorites(brandKey, nextIds);
      setFavoritesIds(nextIds);
      sendToSentry(
        null,
        'Pruned stale favorite listing IDs',
        'favorites',
        'reconcile-prune',
        {
          prunedCount: computation.staleIds.length,
          remainingCount: nextIds.length,
          reasons: computation.reasons,
        }
      );
    });

    return () => {
      cancelled = true;
    };
  }, [brandKey]);

  return (
    <FavoritesContext.Provider
      value={{
        favoritesIds,
        favoritesCount,
        addToFavorites,
        removeFromFavorites,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
